import { useRef, useState } from "react";
import { flexRender } from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";

import Text from "./Text";

const ROW_HEIGHT = 38;
const COLUMN_WIDTH = 130;

//fit: height of its own rows (header included) instead of the fixed 500px, for the small tables under another one
const PlantReportTable = ({ table, theme, fit = false }) => {

    const isDark = theme.White !== "#FFFFFF";
    const rowColor = isDark ? theme.Ey : theme.Ed;
    const hoverColor = isDark ? theme.FOURFOUR : theme.Ey;
    const getWidth = (columnDef) => columnDef.width ?? COLUMN_WIDTH;
    const totalWidth = table.getVisibleLeafColumns().reduce((sum, col) => sum + getWidth(col.columnDef), 0);
    const [hoveredRow, setHoveredRow] = useState(null);

    const styles = {
        row: {
            display: 'flex', width: '100%', minWidth: `${totalWidth}px`, height: `${ROW_HEIGHT}px`, alignItems: 'center',
            boxSizing: 'border-box', backgroundColor: rowColor, border: `0.5px solid ${theme.Ey}`, borderRadius: '4px'
        },
        colWrapper: (columnDef) => ({
            flex: `${getWidth(columnDef)} 1 ${getWidth(columnDef)}px`, minWidth: `${getWidth(columnDef)}px`, display: 'flex', alignItems: 'center', boxSizing: 'border-box',
            padding: '0 16px', justifyContent: columnDef.type === 'text' ? 'flex-start' : 'flex-end'
        }),
        truncate: {
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '100%'
        },
        headerTruncate: {
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0
        }
    }

    const containerRef = useRef(null);
    const {rows} = table.getRowModel();
    const rowVirtualizer = useVirtualizer({
        count: rows.length,
        getScrollElement: () => containerRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: 10
    })

    return (
        <div ref={containerRef} style={{width: '100%', minWidth: 0, height: fit ? `${(rows.length + 1) * ROW_HEIGHT + 20}px` : '500px', overflowY: 'auto', overflowX: 'auto', scrollbarGutter: 'stable'}}>
            <div style={{width: '100%', minWidth: `${totalWidth}px`, height: `${rowVirtualizer.getTotalSize() + ROW_HEIGHT}px`, position: 'relative'}}>
                <div style={{position: 'sticky', top: 0, zIndex: 1, backgroundColor: rowColor}}>
                    {table.getHeaderGroups().map(headerGroup => (
                        <div key={headerGroup.id} style={styles.row}>
                            {headerGroup.headers.map(header => {
                                const isSorted = header.column.getIsSorted();
                                const sortIcon = isSorted === 'asc' ? ' ▲' : isSorted === 'desc' ? ' ▼' : '';
                                return (
                                    <div key={header.id} onClick={header.column.getToggleSortingHandler()} title={header.column.columnDef.note ?? header.column.columnDef.header} style={{...styles.colWrapper(header.column.columnDef), height: '36px', cursor: 'pointer', userSelect: 'none'}}>
                                        <Text size={15} weigth={500} color={theme.ONED} style={styles.headerTruncate}>{flexRender(header.column.columnDef.header, header.getContext())}</Text>
                                        <Text size={12} style={{marginLeft: '4px', flex: 'none'}}>{sortIcon}</Text>
                                    </div>
                                )
                            })}
                        </div>
                    ))}
                </div>
                {rowVirtualizer.getVirtualItems().map(virtualRow => {
                    const row = rows[virtualRow.index];
                    return (
                        <div key={row.id} onMouseEnter={() => setHoveredRow(row.id)} onMouseLeave={() => setHoveredRow(null)} style={{position: 'absolute', top: ROW_HEIGHT, left: 0, width: '100%', minWidth: `${totalWidth}px`, transform: `translateY(${virtualRow.start}px)`}}>
                            <div style={{...styles.row, backgroundColor: hoveredRow === row.id ? hoverColor : rowColor}}>
                                {row.getVisibleCells().map(cell => {
                                    const accessorKey = cell.column.columnDef.accessorKey;
                                    const content = flexRender(cell.column.columnDef.cell, cell.getContext());
                                    //A 0% bonus in red, as in Bonos
                                    const color = accessorKey === 'bono' && cell.getValue() === 0 ? theme.Red : theme.ONED;
                                    return (
                                        <div key={cell.id} style={styles.colWrapper(cell.column.columnDef)} title={content}>
                                            <Text color={color} size={15} style={{...styles.truncate, textAlign: cell.column.columnDef.type === 'text' ? 'left' : 'right'}}>{content}</Text>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
};

export default PlantReportTable;